import { useToast } from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import extendedWarrantyService from "@/services/extendedWarrantyService";
import productService from "@/services/productService";

import CustomerInfoForm from "@/components/extendedWarranty/add/customerInfoForm";
import Loading from "@/components/extendedWarranty/add/loading";
import PlanPicker from "@/components/extendedWarranty/add/planPicker";
import ProductInfoForm from "@/components/extendedWarranty/add/productInfoForm";
import {
  COLLECTION_TYPES,
  normalizeCollectionTypes,
} from "@/components/extendedWarranty/packages/collectionTypesField";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";

/**
 * EPS-4970 revize — Merchant panelinden ek hizmet satışı.
 *
 * Sayfa tek ekran; bölümler aşağı doğru açılır:
 *   1. Ürün Bilgisi (ana kategori, kategori, ürün fiyatı) → "İlerle"
 *   2. Eşleşen Paketler (barem/resolve sonucu) → paket seçimi
 *   3. Ürün ve Müşteri Bilgileri (marka, ürün adı, ad, soyad, TCKN, telefon/e-posta,
 *      fatura) → "Satışı Tamamla" / "Taslak Olarak Kaydet"
 *
 * <b>Ürün en sonda çözülür.</b> Plan eşleştirmesi yalnızca kategori zinciri + fiyat
 * istiyor (barem/resolve'un tüm kategori parametreleri opsiyonel), bu yüzden marka ve
 * ürün adı plan seçiminden sonra sorulabiliyor. "Satışı Tamamla"da sırayla: ürün
 * çözümle/oluştur (product-api bul-veya-oluştur, ürün kodunu kendisi üretir) → özet →
 * satış. Bunun bedeli, panelde ürün/marka seviyesinde tanımlı planların artık
 * listelenmemesi: o seviyeler ancak ürün önceden biliniyorsa eşleşiyor.
 *
 * Tutar `/seller-screens/summary` ile BE'de hesaplanır ve hiç saklanmaz; satış o
 * kalemlerle gönderilir. Özet olmadan bff satırı fiyatlandıramaz. Tutar panelde
 * gösterilmiyor, ama çağrı satışın kalemlerini ürettiği için zorunlu.
 */
const NewPackage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [productInfo, setProductInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansError, setPlansError] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [draftPaymentStatus, setDraftPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // Taslaktan devam etme. draftId URL'den gelir (satış listesindeki Taslaklar
  // sekmesinden). customerDefaults yalnızca hidrasyonda dolar; ürün+müşteri formu
  // taslak yüklendikten sonra mount olduğu için defaultValues olarak yeterli.
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get("draftId");
  const [draftId, setDraftId] = useState(draftIdParam ? Number(draftIdParam) : null);
  const [customerDefaults, setCustomerDefaults] = useState(null);
  const [draftHydrating, setDraftHydrating] = useState(Boolean(draftIdParam));

  // BE hata mesajı bazen i18n anahtarı olarak dönüyor (ör.
  // "plan.price.range.not.matched") — Locize'da karşılığı varsa çevir, yoksa ham
  // mesajı göster.
  const messageOf = (err) => {
    const raw = err?.response?.data?.errors?.[0]?.message;
    return raw ? t(raw, raw) : null;
  };

  const showError = (err) => {
    if (err?.saleInvoiceUploadFailed) {
      toast({
        title:
          messageOf(err.cause) ||
          t(
            "extendedWarranty.add.saleInvoiceUploadError",
            "Fatura yüklenemedi. Lütfen tekrar deneyin.",
          ),
        variant: "error",
      });
      return;
    }
    toast({
      title:
        messageOf(err) ||
        t("common.unexpectedError", "Beklenmeyen bir hata oluştu"),
      variant: "error",
    });
  };

  const resolveSaleInvoice = async (values, keepUploadedSaleInvoice) => {
    const pending = values.saleInvoiceFile;
    if (!pending?.file) return values;
    let uploaded;
    try {
      uploaded = await extendedWarrantyService.uploadSaleInvoice(pending.file);
      if (!uploaded?.path) throw new Error("Sale invoice upload returned no path");
    } catch (cause) {
      throw Object.assign(new Error("Sale invoice upload failed"), {
        saleInvoiceUploadFailed: true,
        cause,
      });
    }
    const saleInvoiceFile = {
      path: uploaded.path,
      url: uploaded.url,
      name: pending.name,
    };
    keepUploadedSaleInvoice?.(saleInvoiceFile);
    return { ...values, saleInvoiceFile };
  };

  const resetProductStep = () => {
    setProductInfo(null);
    setPlans([]);
    setPlansError(null);
    setSelectedPlan(null);
  };

  const noMatchingPlansText = () =>
    t(
      "extendedWarranty.add.noMatchingPlans",
      "Bu ürün ve fiyat için eşleşen ek hizmet paketi bulunamadı. Paketin fiyat aralığını ve kategorisini kontrol edebilirsiniz.",
    );

  /**
   * Fiyat aralığı hiç tanımlanmamış ESKİ planlar için yedek liste.
   *
   * `barem/resolve` yalnızca fiyat aralığı olan planları döner (BAREM satırları ve
   * oransal aralığı olan Standart Planlar). Aralığı olmayan eski planlarla çalışan
   * bir merchant, yeni ekranda hiçbir paket göremeyip satış yapamaz hâle gelirdi —
   * eski ekran tüm aktif paketleri dropdown'da listeliyordu. Bu yüzden eşleşme
   * bulunamadığında bu planları ayrıca listeliyoruz.
   *
   * Tutar bu planlarda plan tanımından çözülemez; bff satış özetinde alt kategori
   * yüzdesinden hesaplar (eski davranış). Bu yüzden kartta fiyat yazmaz.
   */
  const loadPlansWithoutPriceRange = async () => {
    const response = await extendedWarrantyService.getAdditionalServicePlans({
      page: 1,
      size: 200,
      status: "ACTIVE",
    });
    return (response?.content || [])
      .filter(
        (plan) =>
          plan.planType !== "BAREM" && !(plan.rateBarems || []).length,
      )
      .map((plan) => ({
        planId: plan.id,
        name: plan.name,
        durationMonth: plan.durationMonth,
        planDescription: plan.planDescription,
        packageCategory: plan.packageCategory,
        contractPdfUrl: plan.contractPdfUrl,
        // EPS-4971: tahsilat yöntemi — satış aksiyonunu belirler.
        collectionTypes: plan.collectionTypes,
        // Fiyat ve eşleşme seviyesi yok — kart bunu "tutar seçimde hesaplanır"
        // olarak gösterir.
        planPrice: null,
        matchLevel: null,
      }));
  };

  /**
   * Plan eşleştirmesi: kategori zinciri + fiyat. Hem "İlerle" adımından hem taslaktan
   * devam etme akışından çağrılıyor, ikisinde de ürün henüz yok.
   *
   * Eşleştirme başarısızsa ürün adımı açık kalır: kullanıcı "Düzenle" ile fiyatı/
   * kategoriyi değiştirip tekrar deneyebilsin diye hata bölüm içinde gösterilir.
   * Eşleşme olmaması hata değil, boş liste olarak gelir.
   */
  const loadPlansForCategory = async (info) => {
    setPlansLoading(true);
    try {
      const matchedPlans = await extendedWarrantyService.resolveBaremPlans({
        mainCategoryId: Number(info.mainCategoryId),
        categoryId: info.categoryId ? Number(info.categoryId) : undefined,
        price: Number(info.productPrice),
      });
      if (matchedPlans?.length) {
        setPlans(matchedPlans);
        return matchedPlans;
      }
      const legacyPlans = await loadPlansWithoutPriceRange();
      setPlans(legacyPlans);
      setPlansError(
        legacyPlans.length
          ? t(
              "extendedWarranty.add.onlyPlansWithoutRange",
              "Ürün fiyatına uyan bir fiyat aralığı bulunamadı. Aşağıda fiyat aralığı tanımlanmamış aktif paketler listelenmiştir; tutar paket seçildiğinde hesaplanır.",
            )
          : noMatchingPlansText(),
      );
      return legacyPlans;
    } catch (err) {
      setPlans([]);
      setPlansError(messageOf(err) || noMatchingPlansText());
      return [];
    } finally {
      setPlansLoading(false);
    }
  };

  const handleProductInfoSubmit = async (data) => {
    setPlansError(null);
    setSelectedPlan(null);
    setProductInfo(data);
    await loadPlansForCategory(data);
  };

  const handlePlanSelect = (plan) => setSelectedPlan(plan);

  /**
   * EPS-4971: Planın tahsilat yöntemi. Alan boş gelen eski planlar bayi tahsilatı
   * sayılır (bugüne kadarki tek davranış). Yalnızca ONLINE tanımlıysa bayi satışı
   * kendisi tamamlayamaz — bff de aynı kuralı uyguluyor (SALE_REQUIRES_ONLINE_PAYMENT).
   */
  const sellerCollectionAllowed = normalizeCollectionTypes(
    selectedPlan?.collectionTypes,
  ).includes(COLLECTION_TYPES.SELLER);

  /**
   * EPS-4971: Planda online ödeme linki tanımlıysa bayi müşteriye link gönderebilir.
   * İkisi de tanımlıysa yöntemi bayi seçiyor (analiz F4) — teknik olarak bayinin POS'tan
   * çekip çekmediğini bilemediğimiz için seçim ona bırakılıyor.
   */
  const paymentLinkAllowed = normalizeCollectionTypes(
    selectedPlan?.collectionTypes,
  ).includes(COLLECTION_TYPES.ONLINE);

  /**
   * Taslaktan devam etme. Sıra önemli: kategori/fiyat state'i kurulur, planlar yeniden
   * eşleştirilir (bayi planı değiştirebilsin diye PlanPicker görünür kalmalı), seçili
   * plan geri yazılır. Tutar taslakta saklanmaz, satış anında BE'den gelir.
   *
   * Taslak okunamazsa (silinmiş, başka merchant'a ait) akış boş "Satış Ekle" olarak
   * devam eder — bayi baştan girer, hata ekranı göstermek gereksiz.
   */
  useEffect(() => {
    if (!draftIdParam) return;

    let cancelled = false;

    (async () => {
      try {
        const draft = await extendedWarrantyService.getSaleDraft(draftIdParam);
        const snapshot = draft?.productSnapshot;
        if (cancelled || !snapshot?.productInfo) {
          setDraftHydrating(false);
          return;
        }

        setProductInfo(snapshot.productInfo);
        setCustomerDefaults(draft.customerSnapshot || null);
        // EPS-4971: ödeme linki gönderilmiş/ödemesi alınmış taslakta elle tahsilat kapalı.
        setDraftPaymentStatus(draft.paymentStatus || null);

        const freshPlans = await loadPlansForCategory(snapshot.productInfo);
        if (cancelled) return;

        if (snapshot.selectedPlan) {
          // EPS-4971: taslak alındıktan sonra planın tahsilat yöntemi (ya da fiyat
          // aralığı) değişmiş olabilir; aynı plan yeniden eşleşiyorsa taslaktaki
          // eski kopya yerine BE'den gelen güncel hâli kullanılır.
          const fresh = (freshPlans || []).find(
            (plan) => plan.planId === snapshot.selectedPlan.planId,
          );
          setSelectedPlan(fresh || snapshot.selectedPlan);
        }
      } catch {
        // Sessiz: taslak yüklenemedi, akış sıfırdan devam eder.
      } finally {
        if (!cancelled) setDraftHydrating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftIdParam]);

  /**
   * Ürünü çözer/oluşturur. Mevcut ürün seçildiyse yeniden çözümlemeye gerek yok; yeni
   * ad yazıldıysa BE oluşturur (aynı veriyle ikinci çağrı mevcut ürünü döner).
   */
  const resolveProduct = (values) =>
    values.selectedProduct ||
    productService.resolveProductForSale({
      mainCategoryId: Number(productInfo.mainCategoryId),
      categoryId: productInfo.categoryId ? Number(productInfo.categoryId) : null,
      brandName: values.brandName,
      productName: values.productName,
      price: Number(productInfo.productPrice),
    });

  const fetchSummaryFor = (plan, product) =>
    extendedWarrantyService.getSaleSummary([
      {
        merchantProductId: Number(product.id),
        merchantAdditionalServicePlanId: Number(plan.planId),
        productPrice: Number(productInfo.productPrice),
        productName: product.name,
        additionalPlanName: plan.name,
      },
    ]);

  /**
   * "Taslak Olarak Kaydet". Doğrulama yok — taslak tanımı gereği yarım veridir, bu
   * yüzden ürün de oluşturulmaz: marka/ürün adı taslakta metin olarak durur, gerçek
   * `merchant_product` satırı ancak satış tamamlanınca açılır.
   *
   * `draftId` tutuluyor: aynı sayfada ikinci kez kaydedilirse yeni taslak açmaz,
   * aynısını günceller. Boş gönderildiğinde BE her zaman yeni satır açar.
   *
   * EPS-5097: kayıttan sonra Satışlar listesine dönülür; taslak orada "Taslak Satış"
   * olarak görünür ve tıklanınca sihirbaz kaldığı yerden açılır.
   */
  const handleSaveDraft = async (formValues, keepUploadedSaleInvoice) => {
    setSavingDraft(true);
    try {
      const values = await resolveSaleInvoice(
        formValues,
        keepUploadedSaleInvoice,
      );
      const saved = await extendedWarrantyService.upsertSaleDraft({
        draftId,
        merchantProductId: values.selectedProduct?.id
          ? Number(values.selectedProduct.id)
          : null,
        merchantAdditionalServicePlanId: Number(selectedPlan.planId),
        productSnapshot: { productInfo, selectedPlan },
        customerSnapshot: values,
      });
      if (saved?.id) setDraftId(saved.id);
      toast({
        title: t("extendedWarranty.add.draftSaved", "Satış taslak olarak kaydedildi"),
        variant: "success",
      });
      navigate("/extended-warranties/transactions");
    } catch (err) {
      showError(err);
    } finally {
      setSavingDraft(false);
    }
  };

  /**
   * "Ödeme Linki Gönder": ürün çözümle → özet → link gönder.
   *
   * Satış BURADA OLUŞMUYOR: müşteri ödemeyi tamamladığında ödeme webhook'u üzerinden
   * oluşuyor. Ürünün burada çözülmesi bilinçli — ödeme webhook'unda panel bağlamı
   * olmadığı için ürün id'si linkten önce belirlenmiş olmalı.
   */
  const handleSendPaymentLink = async (
    formValues,
    channel,
    keepUploadedSaleInvoice,
  ) => {
    setLoading(true);
    try {
      const product = await resolveProduct(formValues);
      const summary = await fetchSummaryFor(selectedPlan, product);
      const items = summary?.items;
      if (!items?.length) {
        toast({
          title: t(
            "extendedWarranty.add.summaryMissing",
            "Paket tutarı hesaplanamadı, lütfen paketi tekrar seçin.",
          ),
          variant: "error",
        });
        return;
      }

      const values = await resolveSaleInvoice(
        formValues,
        keepUploadedSaleInvoice,
      );

      // Taslak ONCE sihirbazin kendi sekliyle kaydedilir ({productInfo, selectedPlan} +
      // form degerleri): satis listesi ve "detaya gir" hidrasyonu bu sekli okuyor.
      // bff link gonderirken dolu snapshot'a dokunmuyor.
      const savedDraft = await extendedWarrantyService.upsertSaleDraft({
        draftId,
        merchantProductId: product?.id ? Number(product.id) : null,
        merchantAdditionalServicePlanId: Number(selectedPlan.planId),
        productSnapshot: { productInfo, selectedPlan },
        customerSnapshot: values,
      });
      if (savedDraft?.id) setDraftId(savedDraft.id);

      const response =
        await extendedWarrantyService.sendSalePaymentLinkForIndividualCustomer(
          {
            firstName: values.firstName,
            lastName: values.lastName,
            identificationNumber: values.identificationNumber,
            phoneNumber: values.phone || null,
            email: values.email || null,
            invoiceNumber: values.invoiceNumber,
            invoiceDate: values.invoiceDate.split("-").reverse().join("-"),
            items,
            draftId: savedDraft?.id ?? draftId,
            // EPS-5097: satış ödeme sonrası oluşurken bu gövde aynen kullanılıyor.
            salesPersonnelName: values.salesPersonnelName,
            saleInvoiceFilePath: values.saleInvoiceFile?.path ?? null,
          },
          channel,
        );

      toast({
        title: t(
          "extendedWarranty.add.paymentLinkSent",
          "Ödeme linki gönderildi: {{target}}",
          { target: response?.maskedTarget || "" },
        ),
        variant: "success",
      });
      navigate("/extended-warranties/transactions");
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * "Satışı Tamamla": ürün çözümle → özet → satış. Üçü de burada, çünkü ürün ancak bu
   * adımda (marka + ürün adı girildikten sonra) biliniyor.
   */
  const handleCustomerInfoSubmit = async (
    formValues,
    keepUploadedSaleInvoice,
  ) => {
    setLoading(true);
    try {
      const product = await resolveProduct(formValues);
      const summary = await fetchSummaryFor(selectedPlan, product);
      const items = summary?.items;
      if (!items?.length) {
        toast({
          title: t(
            "extendedWarranty.add.summaryMissing",
            "Paket tutarı hesaplanamadı, lütfen paketi tekrar seçin.",
          ),
          variant: "error",
        });
        return;
      }

      const values = await resolveSaleInvoice(
        formValues,
        keepUploadedSaleInvoice,
      );

      await extendedWarrantyService.createSaleForIndividualCustomer({
        firstName: values.firstName,
        lastName: values.lastName,
        identificationNumber: values.identificationNumber,
        // Boş bırakılan iletişim alanı hiç gönderilmiyor; BE "en az biri" kuralını
        // uyguluyor ve boş olanın yerine yer tutucu üretiyor.
        phoneNumber: values.phone || null,
        email: values.email || null,
        invoiceNumber: values.invoiceNumber,
        // BE sözleşmesi dd-MM-yyyy; input yyyy-MM-dd veriyor.
        invoiceDate: values.invoiceDate.split("-").reverse().join("-"),
        items,
        // Taslaktan tamamlanıyorsa bff satış başarılı olunca taslağı siler.
        draftId,
        // EPS-5097: extended_warranty.personal_name — Personel Performansı raporu.
        salesPersonnelName: values.salesPersonnelName,
        saleInvoiceFilePath: values.saleInvoiceFile?.path ?? null,
      });

      toast({
        title: t("extendedWarranty.add.created", "Satış oluşturuldu"),
        variant: "success",
      });
      navigate("/extended-warranties/transactions");
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col bg-[#F9FAFC]">
      {loading && (
        <Loading
          title={t(
            "extendedWarrantiesPage.loading",
            "egaranti Plus Hazırlanıyor...",
          )}
        />
      )}
      <Breadcrumb>
        <BreadcrumbItem link="/extended-warranties/transactions">
          {t("extendedWarrantiesPage.title")}
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t("extendedWarrantiesPage.addSale", "Satış Ekle")}
        </BreadcrumbItem>
      </Breadcrumb>
      <div className="mx-auto mb-16 mt-8 flex w-full max-w-[1100px] flex-col gap-6 px-4">
        <ProductInfoForm
          onSubmit={handleProductInfoSubmit}
          onReset={resetProductStep}
          defaultValues={productInfo}
          locked={Boolean(productInfo)}
          loading={loading}
        />

        {(plansLoading || plansError || plans.length > 0) && (
          <PlanPicker
            plans={plans}
            loading={plansLoading}
            error={plansError}
            selectedPlanId={selectedPlan?.planId}
            onSelect={handlePlanSelect}
          />
        )}

        {selectedPlan && !draftHydrating && (
          <CustomerInfoForm
            onSubmit={handleCustomerInfoSubmit}
            onSaveDraft={handleSaveDraft}
            savingDraft={savingDraft}
            loading={loading}
            initialValues={customerDefaults}
            productInfo={productInfo}
            sellerCollectionAllowed={sellerCollectionAllowed}
            paymentLinkAllowed={paymentLinkAllowed}
            onSendPaymentLink={handleSendPaymentLink}
            draftPaymentStatus={draftPaymentStatus}
          />
        )}
      </div>
    </main>
  );
};

export default NewPackage;
